/**
 * GLOBAL-FILTER-ENGINE-000001 — the single business context strip (shell-level).
 * FOUNDER-DASHBOARD-REDESIGN-IMPLEMENTATION-000001: context lives here and nowhere
 * else. One row, self-describing controls, no page-owned duplicates.
 */
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { shell } from '../shell/shellClasses';
import { useShell } from '../shell/ShellProvider';
import { COMPARE_OPTIONS, DATE_RANGE_OPTIONS } from './defaults';
import { useGlobalFilter } from './GlobalFilterProvider';
import type { UniversalBusinessContext } from './types';

const selectCls = `${shell.control} ${shell.focusRing} min-w-0 max-w-[12rem] border-transparent bg-transparent hover:bg-[var(--shell-hover)]`;
const panelInputCls = `${shell.control} ${shell.focusRing} w-full min-w-0`;

function Popover({
  label,
  ariaLabel,
  children,
}: {
  label: string;
  ariaLabel: string;
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        className={`${shell.control} ${shell.focusRing} border-transparent bg-transparent ${shell.muted} ${shell.hoverRow}`}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((v) => !v)}
      >
        {label}
      </button>
      {open && (
        <div
          role="dialog"
          aria-label={ariaLabel}
          className={`absolute right-0 z-50 mt-1 w-[min(90vw,32rem)] rounded-[var(--shell-radius-md)] border ${shell.border} ${shell.surfaceElevated} p-3 shadow-lg`}
        >
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}

export function GlobalFilterBar() {
  const formId = useId();
  const {
    context,
    setFilters,
    resetFilters,
    savedViews,
    saveCurrentView,
    applyView,
    deleteView,
  } = useGlobalFilter();
  const { connectionOptions } = useShell();
  const [saveName, setSaveName] = useState('');

  const set = <K extends keyof UniversalBusinessContext>(
    key: K,
    value: UniversalBusinessContext[K],
  ) => setFilters({ [key]: value } as Partial<UniversalBusinessContext>);

  return (
    <div
      role="region"
      aria-label="Business context"
      className={`border-b ${shell.border} bg-[var(--shell-context)] px-3 py-1.5 lg:px-6`}
    >
      <div className="flex flex-wrap items-center gap-1">
        <select
          id={`${formId}-seller`}
          aria-label="Store"
          title="Store"
          className={selectCls}
          value={context.sellerLabel === '—' ? '' : context.sellerLabel}
          onChange={(e) =>
            setFilters({
              sellerLabel: e.target.value || '—',
              sellerId: e.target.value || null,
            })
          }
        >
          <option value="">All stores</option>
          <option value="Primary">Primary</option>
          <option value="Secondary">Secondary</option>
        </select>

        <select
          id={`${formId}-mp`}
          aria-label="Marketplace"
          title="Marketplace"
          className={selectCls}
          value={context.marketplace}
          onChange={(e) => set('marketplace', e.target.value)}
        >
          <option value="Amazon">Amazon</option>
          <option value="Amazon US">Amazon US</option>
          <option value="Amazon IN">Amazon IN</option>
        </select>

        {connectionOptions.length > 0 ? (
          <select
            id={`${formId}-conn`}
            aria-label="Connection"
            title="Connection"
            className={selectCls}
            value={context.connectionId ?? ''}
            onChange={(e) => set('connectionId', e.target.value || null)}
          >
            {connectionOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        ) : null}

        <span aria-hidden className={`px-1 ${shell.subtle}`}>
          ·
        </span>

        <select
          id={`${formId}-date`}
          aria-label="Date range"
          title="Date range"
          className={selectCls}
          value={context.dateRange}
          onChange={(e) =>
            set('dateRange', e.target.value as UniversalBusinessContext['dateRange'])
          }
        >
          {DATE_RANGE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>

        <select
          id={`${formId}-cmp`}
          aria-label="Compare with"
          title="Compare with"
          className={selectCls}
          value={context.comparePeriod}
          onChange={(e) =>
            set('comparePeriod', e.target.value as UniversalBusinessContext['comparePeriod'])
          }
        >
          {COMPARE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>

        <div className="ml-auto flex items-center gap-1">
          <Popover label="Views" ariaLabel="Saved views">
            {(close) => (
              <div className="space-y-3">
                {savedViews.length > 0 ? (
                  <ul className="space-y-1" aria-label="Saved views">
                    {savedViews.map((v) => (
                      <li key={v.id} className="flex items-center gap-2">
                        <button
                          type="button"
                          className={`flex-1 truncate rounded px-2 py-1.5 text-left text-sm ${shell.hoverRow} ${shell.focusRing}`}
                          onClick={() => {
                            applyView(v.id);
                            close();
                          }}
                        >
                          {v.pinned ? '★ ' : ''}
                          {v.name}
                        </button>
                        <button
                          type="button"
                          className={`rounded px-2 py-1 text-xs text-[var(--shell-danger)] ${shell.focusRing}`}
                          aria-label={`Delete view ${v.name}`}
                          onClick={() => deleteView(v.id)}
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className={`text-sm ${shell.muted}`}>No saved views yet.</p>
                )}
                <div className="flex gap-1">
                  <input
                    id={`${formId}-save`}
                    className={panelInputCls}
                    placeholder="Name this view"
                    aria-label="Name this view"
                    value={saveName}
                    onChange={(e) => setSaveName(e.target.value)}
                  />
                  <button
                    type="button"
                    className={`${shell.control} ${shell.focusRing} shrink-0`}
                    onClick={() => {
                      if (!saveName.trim()) return;
                      saveCurrentView(saveName, { pinned: false });
                      setSaveName('');
                    }}
                  >
                    Save
                  </button>
                </div>
              </div>
            )}
          </Popover>

          <Popover label="Filters" ariaLabel="Additional filters">
            {() => (
              <div className="space-y-3">
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {(
                    [
                      ['warehouseId', 'Warehouse'],
                      ['state', 'State'],
                      ['city', 'City'],
                      ['category', 'Category'],
                      ['subCategory', 'Sub category'],
                      ['brand', 'Brand'],
                      ['asin', 'ASIN'],
                      ['sku', 'SKU'],
                      ['fnsku', 'FNSKU'],
                      ['productType', 'Product type'],
                      ['orderStatus', 'Order status'],
                      ['inventoryStatus', 'Inventory status'],
                      ['pricingStatus', 'Pricing status'],
                      ['returnStatus', 'Return status'],
                      ['businessUnit', 'Business unit'],
                    ] as const
                  ).map(([key, label]) => (
                    <div key={key} className="flex flex-col gap-1">
                      <label
                        htmlFor={`${formId}-${key}`}
                        className={`text-xs font-medium ${shell.muted}`}
                      >
                        {label}
                      </label>
                      <input
                        id={`${formId}-${key}`}
                        className={panelInputCls}
                        value={(context[key] as string | null) ?? ''}
                        onChange={(e) => set(key, e.target.value.trim() || null)}
                        autoComplete="off"
                      />
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  className={`${shell.control} ${shell.focusRing}`}
                  onClick={() => resetFilters()}
                >
                  Reset all filters
                </button>
              </div>
            )}
          </Popover>
        </div>
      </div>
    </div>
  );
}
